import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import type { CTHMeasurementPair, CTHThreadInspectionData, CTHVisualResult } from '@/types/cthThreadTypes'
import { cthIncludesBox, cthIncludesPin, dimensionalRowsForKind, visualRowsForKind } from '@/types/cthThreadTypes'
import { drawMethodTitle } from './pdfTitle'
import {
  formatDate,
  formatDateDDMMYYYY,
  getBase64Image,
  getFullName,
  getLogoDataUrl,
  getSignature,
} from './ndtPdfShared'

const UNUSED_SIDE = '---'

function dimCell(value: string | undefined, active = true): string {
  if (!active) return UNUSED_SIDE
  return value?.trim() ? value.trim() : '-'
}

function visualCell(value: CTHVisualResult | 'N/A' | undefined, active = true): string {
  if (!active) return UNUSED_SIDE
  if (value === 'N/A') return 'N/A'
  return value === 'REJ' ? 'REJ' : value === 'OK' ? 'OK' : '-'
}

function getImageFormat(src: string): 'JPEG' | 'PNG' | 'WEBP' {
  const lower = src.slice(0, 40).toLowerCase()
  if (lower.includes('image/png')) return 'PNG'
  if (lower.includes('image/webp')) return 'WEBP'
  return 'JPEG'
}

function fitImageInBox(naturalPxW: number, naturalPxH: number, maxWmm: number, maxHmm: number) {
  const PX_TO_MM = 25.4 / 96
  let width = Math.max(1, naturalPxW) * PX_TO_MM
  let height = Math.max(1, naturalPxH) * PX_TO_MM
  if (width > maxWmm) {
    const scale = maxWmm / width
    width = maxWmm
    height *= scale
  }
  if (height > maxHmm) {
    const scale = maxHmm / height
    height = maxHmm
    width *= scale
  }
  return { width, height }
}

export async function generateCTHThreadInspectionPDF(data: CTHThreadInspectionData): Promise<Blob> {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const pageWidth = pdf.internal.pageSize.getWidth()
  const margin = 15

  const kind = data.inspectionKind === 'RSC' ? 'RSC' : 'CTH'
  drawMethodTitle(pdf, `${kind} Thread`, margin, margin, { trailing: 'Inspection' })
  const logoDataUrl = await getLogoDataUrl()
  if (logoDataUrl) {
    pdf.addImage(logoDataUrl, 'PNG', pageWidth - margin - 40, margin - 5, 40, 12)
  }

  const verifierName = (data.verifiedBy || '').trim()
  const [inspectorSignature, verifierSignature] = await Promise.all([
    getSignature(data.issuedBy || data.inspectorName || ''),
    verifierName ? getSignature(verifierName) : Promise.resolve(null),
  ])

  let currentY = margin + 15
  const tableWidth = pageWidth - 2 * margin
  const pinActive = cthIncludesPin(data.inspectedSides)
  const boxActive = cthIncludesBox(data.inspectedSides)
  const col4 = tableWidth / 4
  const col6 = tableWidth / 6
  const summaryLabelW = col6
  const summaryMidW = tableWidth / 2 - col6 / 2
  const summaryRightW = tableWidth - summaryLabelW - summaryMidW
  const lastY = () => (pdf as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY

  autoTable(pdf, {
    startY: currentY,
    body: [
      [{ content: 'INSPECTION REPORT', colSpan: 4, styles: { halign: 'center' as const, fontStyle: 'bold' as const, fontSize: 5 } }],
      ['Equipment Owner:', data.companyName || '', 'Acceptance:', data.acceptance || ''],
      ['Object Description:', data.objectDescription || '', 'Report No:', data.reportNo || ''],
      ['Assembly Description:', data.assemblyDescription || '', 'Test site:', data.testSite || ''],
      ['Material:', data.material || '', 'Test date:', data.testDate ? formatDate(data.testDate) : ''],
      ['Serial No.:', data.serialNo || '', 'Operator:', data.operator || ''],
      ['Part No.:', data.partNo || '', 'Test procedure:', data.testProcedure || ''],
      ['Comments:', { content: data.comments || '', colSpan: 3, styles: { fontStyle: 'normal' as const } }],
    ],
    theme: 'grid',
    tableWidth,
    styles: { fontSize: 5, cellPadding: 1, lineColor: [0, 0, 0], lineWidth: 0.1, valign: 'middle' },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: col4, halign: 'left', fillColor: [242, 242, 242] },
      1: { fontStyle: 'normal', cellWidth: col4, halign: 'left' },
      2: { fontStyle: 'bold', cellWidth: col4, halign: 'left', fillColor: [242, 242, 242] },
      3: { fontStyle: 'normal', cellWidth: col4, halign: 'left' },
    },
    margin: { left: margin, right: margin },
  })
  currentY = lastY()

  const includeBodyColumn = kind === 'RSC'
  const dimColCount = includeBodyColumn ? 8 : 6
  const dimLabelW = includeBodyColumn ? tableWidth * 0.15 : col6
  const dimValueW = includeBodyColumn ? (tableWidth - dimLabelW * 3) / 5 : col6
  const dimensionalRows = dimensionalRowsForKind(kind)
  const bodyRowCells = (index: number): [string, string] => {
    if (index === 0) return ['Tool Length', dimCell(data.bodyToolLength, true)]
    if (index === 1) return ['OD', dimCell(data.bodyOd, true)]
    return ['-', '-']
  }
  const dimensionalBody: string[][] = dimensionalRows.map((row, index) => {
    const pinPair: CTHMeasurementPair | undefined = data.pin?.[row.pinField]
    const boxPair: CTHMeasurementPair | undefined = data.box?.[row.boxField]
    const pinCells = [
      row.pinLabel,
      dimCell(pinPair?.no1, pinActive),
      dimCell(pinPair?.no2, pinActive),
    ]
    const boxCells = [
      row.boxLabel,
      dimCell(boxPair?.no1, boxActive),
      dimCell(boxPair?.no2, boxActive),
    ]
    return includeBodyColumn ? [...pinCells, ...bodyRowCells(index), ...boxCells] : [...pinCells, ...boxCells]
  })

  const dimLabelCol = { halign: 'left' as const, cellWidth: dimLabelW, fillColor: [242, 242, 242] as [number, number, number] }
  const dimValueCol = { halign: 'center' as const, cellWidth: dimValueW }

  autoTable(pdf, {
    startY: currentY,
    head: [
      [{ content: 'DIMENSIONAL', colSpan: dimColCount, styles: { halign: 'center' as const, fontStyle: 'bold' as const, fontSize: 5 } }],
      includeBodyColumn
        ? ['PIN', 'No 1:', 'No 2:', { content: 'BODY', colSpan: 2 }, 'BOX', 'No 1:', 'No 2:']
        : ['PIN', 'No 1:', 'No 2:', 'BOX', 'No 1:', 'No 2:'],
    ],
    body: dimensionalBody,
    theme: 'grid',
    styles: { fontSize: 5, cellPadding: 1, valign: 'middle', lineColor: [0, 0, 0], lineWidth: 0.1 },
    headStyles: { fillColor: [242, 242, 242], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 5, halign: 'center' },
    tableWidth,
    columnStyles: includeBodyColumn
      ? {
          0: dimLabelCol,
          1: dimValueCol,
          2: dimValueCol,
          3: dimLabelCol,
          4: dimValueCol,
          5: dimLabelCol,
          6: dimValueCol,
          7: dimValueCol,
        }
      : {
          0: dimLabelCol,
          1: dimValueCol,
          2: dimValueCol,
          3: dimLabelCol,
          4: dimValueCol,
          5: dimValueCol,
        },
    margin: { left: margin, right: margin },
  })
  currentY = lastY()

  const pin = data.visualPin
  const box = data.visualBox
  const visualColCount = includeBodyColumn ? 8 : 6
  const visualBody = visualRowsForKind(kind).map((row) => {
    const pinCells = [
      row.pinLabel,
      visualCell(pin?.[row.pinKey]?.no1, pinActive),
      visualCell(pin?.[row.pinKey]?.no2, pinActive),
    ]
    const boxValue = (slot: 'no1' | 'no2') =>
      row.boxFixed === 'N/A' ? visualCell('N/A', boxActive) : visualCell(box?.[row.boxKey]?.[slot], boxActive)
    const boxCells = [row.boxLabel, boxValue('no1'), boxValue('no2')]
    if (!includeBodyColumn) return [...pinCells, ...boxCells]
    const bodyCells = row.bodyKey && row.bodyLabel
      ? [row.bodyLabel, visualCell(data.visualBody?.[row.bodyKey], true)]
      : ['-', '-']
    return [...pinCells, ...bodyCells, ...boxCells]
  })
  autoTable(pdf, {
    startY: currentY,
    head: [
      [{ content: 'VISUAL', colSpan: visualColCount, styles: { halign: 'center' as const, fontStyle: 'bold' as const, fontSize: 5 } }],
      includeBodyColumn
        ? ['PIN', 'No 1:', 'No 2:', { content: 'BODY', colSpan: 2 }, 'BOX', 'No 1:', 'No 2:']
        : ['PIN', 'No 1:', 'No 2:', 'BOX', 'No 1:', 'No 2:'],
    ],
    body: visualBody,
    theme: 'grid',
    styles: { fontSize: 5, cellPadding: 1, valign: 'middle', lineColor: [0, 0, 0], lineWidth: 0.1 },
    headStyles: { fillColor: [242, 242, 242], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 5, halign: 'center' },
    tableWidth,
    columnStyles: includeBodyColumn
      ? {
          0: dimLabelCol,
          1: dimValueCol,
          2: dimValueCol,
          3: dimLabelCol,
          4: dimValueCol,
          5: dimLabelCol,
          6: dimValueCol,
          7: dimValueCol,
        }
      : {
          0: { halign: 'left', cellWidth: col6, fillColor: [242, 242, 242] },
          1: { halign: 'center', cellWidth: col6 },
          2: { halign: 'center', cellWidth: col6 },
          3: { halign: 'left', cellWidth: col6, fillColor: [242, 242, 242] },
          4: { halign: 'center', cellWidth: col6 },
          5: { halign: 'center', cellWidth: col6 },
        },
    margin: { left: margin, right: margin },
  })
  currentY = lastY()

  if (data.mpiInspection?.length) {
    const mpiBody = []
    for (let index = 0; index < data.mpiInspection.length; index += 3) {
      const group = data.mpiInspection.slice(index, index + 3)
      const cells: Array<string | { content: string; colSpan: number }> = []
      group.forEach((field) => {
        cells.push(field.label, field.value || '-')
      })
      const missing = 3 - group.length
      if (missing > 0) {
        cells.push({ content: '', colSpan: missing * 2 })
      }
      mpiBody.push(cells)
    }
    const mpiLabelW = tableWidth * 0.12
    const mpiValueW = tableWidth * 0.2133
    autoTable(pdf, {
      startY: currentY,
      head: [[{ content: 'MAGNETIC PARTICLE INSPECTION', colSpan: 6, styles: { halign: 'center' as const, fontStyle: 'bold' as const, fontSize: 5 } }]],
      body: mpiBody,
      theme: 'grid',
      styles: { fontSize: 5, cellPadding: 1, valign: 'middle', lineColor: [0, 0, 0], lineWidth: 0.1 },
      headStyles: { fillColor: [242, 242, 242], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 5, halign: 'center' },
      tableWidth,
      columnStyles: {
        0: { halign: 'left', fontStyle: 'bold', cellWidth: mpiLabelW, fillColor: [242, 242, 242] },
        1: { halign: 'left', cellWidth: mpiValueW },
        2: { halign: 'left', fontStyle: 'bold', cellWidth: mpiLabelW, fillColor: [242, 242, 242] },
        3: { halign: 'left', cellWidth: mpiValueW },
        4: { halign: 'left', fontStyle: 'bold', cellWidth: mpiLabelW, fillColor: [242, 242, 242] },
        5: { halign: 'left', cellWidth: mpiValueW },
      },
      margin: { left: margin, right: margin },
    })
    currentY = lastY()
  }

  const acceptedLabel = data.testAcceptedLabel || String(data.testAccepted ?? 0)
  const summaryTotal = (data.testAccepted ?? 0) + (data.testRejected ?? 0)
  const recCountLabel = (count: number) => (count <= 0 ? '0' : count === summaryTotal ? String(count) : `${count} of ${summaryTotal}`)
  const formatRec = (count: number, note: string, label: string): string[] => [
    'Rejected',
    recCountLabel(count),
    `${label}${count && note ? ` ${note}` : ''}`,
  ]
  autoTable(pdf, {
    startY: currentY,
    head: [[{ content: 'TEST SUMMARY', colSpan: 3, styles: { halign: 'center' as const, fontStyle: 'bold' as const, fontSize: 5 } }]],
    body: [
      ['Accepted', acceptedLabel, data.testAcceptedNote || ''],
      formatRec(data.testRefaceRecommended, data.testRefaceNote || '', 'Reface recommended'),
      formatRec(data.testRecutRecommended, data.testRecutNote || '', 'Recut recommended'),
      formatRec(data.testScrapRecommended, data.testScrapNote || '', 'Decomissioning recommended'),
    ],
    theme: 'grid',
    styles: { fontSize: 5, cellPadding: 1, valign: 'middle', lineColor: [0, 0, 0], lineWidth: 0.1 },
    headStyles: { fillColor: [242, 242, 242], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 5, halign: 'center' },
    tableWidth,
    columnStyles: {
      0: { halign: 'left', cellWidth: summaryLabelW, fillColor: [242, 242, 242] },
      1: { halign: 'center', cellWidth: summaryMidW },
      2: { halign: 'left', cellWidth: summaryRightW },
    },
    margin: { left: margin, right: margin },
  })
  currentY = lastY()

  const availableHeight = Math.max(20, 297 - currentY - 40)
  const imagePad = 4
  const maxImageW = tableWidth - imagePad * 2
  const maxImageH = Math.max(12, availableHeight - 10)
  let fittedImage: { data: string; format: 'JPEG' | 'PNG' | 'WEBP'; width: number; height: number } | null = null

  if (data.attachedImage) {
    try {
      const imgData = data.attachedImage.startsWith('data:')
        ? data.attachedImage
        : await getBase64Image(data.attachedImage)
      if (imgData) {
        const props = pdf.getImageProperties(imgData)
        const size = fitImageInBox(props.width, props.height, maxImageW, maxImageH)
        fittedImage = { data: imgData, format: getImageFormat(imgData), width: size.width, height: size.height }
      }
    } catch {
      fittedImage = null
    }
  }

  const imageSectionHeight = fittedImage
    ? Math.min(availableHeight, fittedImage.height + imagePad * 2)
    : Math.min(availableHeight, 40)

  autoTable(pdf, {
    startY: currentY,
    head: [[{ content: 'IMAGES', colSpan: 4, styles: { halign: 'center' as const, fontStyle: 'bold' as const, fontSize: 5 } }]],
    body: [[{ content: '', colSpan: 4, styles: { minCellHeight: imageSectionHeight } }]],
    theme: 'grid',
    styles: { fontSize: 5, cellPadding: 1, valign: 'middle', lineColor: [0, 0, 0], lineWidth: 0.1 },
    headStyles: { fillColor: [242, 242, 242], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 5, halign: 'center' },
    tableWidth,
    columnStyles: { 0: { cellWidth: col4 }, 1: { cellWidth: col4 }, 2: { cellWidth: col4 }, 3: { cellWidth: col4 } },
    margin: { left: margin, right: margin },
  })

  const imagesBodyTop = currentY + 6
  currentY = lastY()

  if (fittedImage) {
    try {
      const bodyH = Math.max(1, currentY - imagesBodyTop - imagePad)
      const aspect = fittedImage.width / Math.max(fittedImage.height, 0.01)
      let finalW = Math.min(fittedImage.width, maxImageW)
      let finalH = Math.min(fittedImage.height, bodyH)
      if (finalW / finalH > aspect) finalW = finalH * aspect
      else finalH = finalW / aspect
      pdf.addImage(fittedImage.data, fittedImage.format, margin + imagePad, imagesBodyTop + imagePad, finalW, finalH)
    } catch {
      // leave images area blank
    }
  }

  const pageHeight = pdf.internal.pageSize.getHeight()
  if (currentY + 25 > pageHeight - 10) {
    pdf.addPage()
    currentY = margin
  }

  const signatureWidth = (pageWidth - 2 * margin) / 2
  const hasVerifier = !!verifierName
  const issuedName = getFullName(data.issuedBy || data.inspectorName || '')
  const dateLabel = data.testDate ? formatDateDDMMYYYY(data.testDate) : ''
  pdf.setDrawColor(0)
  pdf.setLineWidth(0.1)

  if (hasVerifier) {
    pdf.rect(margin, currentY, signatureWidth, 25)
    pdf.rect(margin + signatureWidth, currentY, signatureWidth, 25)
    pdf.setFontSize(7)
    pdf.setFont('helvetica', 'bold')
    pdf.text('ISSUED BY', margin + signatureWidth / 2, currentY + 4, { align: 'center' })
    pdf.text('VERIFIED BY', margin + signatureWidth + signatureWidth / 2, currentY + 4, { align: 'center' })
  } else {
    pdf.rect(margin, currentY, pageWidth - 2 * margin, 25)
    pdf.setFontSize(7)
    pdf.setFont('helvetica', 'bold')
    pdf.text('ISSUED BY', margin + (pageWidth - 2 * margin) / 2, currentY + 4, { align: 'center' })
  }

  if (inspectorSignature) {
    pdf.addImage(inspectorSignature, 'PNG', hasVerifier ? margin + signatureWidth / 2 - 20 : pageWidth / 2 - 20, currentY + 8, 40, 10)
  }
  if (hasVerifier && verifierSignature) {
    pdf.addImage(verifierSignature, 'PNG', margin + signatureWidth + signatureWidth / 2 - 20, currentY + 8, 40, 10)
  }

  pdf.setFontSize(8)
  pdf.setFont('helvetica', 'normal')
  if (hasVerifier) {
    pdf.text(`${issuedName} / ${dateLabel} / Ågotnes`, margin + signatureWidth / 2, currentY + 22, { align: 'center' })
    pdf.text(`${getFullName(verifierName)} / ${dateLabel} / Ågotnes`, margin + signatureWidth + signatureWidth / 2, currentY + 22, { align: 'center' })
  } else {
    pdf.text(`${issuedName} / ${dateLabel} / Ågotnes`, pageWidth / 2, currentY + 22, { align: 'center' })
  }

  return pdf.output('blob')
}

export async function generateCTHReport(data: CTHThreadInspectionData): Promise<Blob> {
  return generateCTHThreadInspectionPDF(data)
}
